import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polyline, useMap } from 'react-leaflet';
import { Satellite, Globe, Clock, MapPin, Gauge, Activity } from 'lucide-react';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';

// Fix Leaflet icon
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const ISSTracker = () => {
  const [issData, setIssData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdate, setLastUpdate] = useState(new Date());
  const [trackPath, setTrackPath] = useState([]);
  const [autoTrack] = useState(true);

  const fetchISS = async () => {
    try {
      const res = await fetch('https://api.wheretheiss.at/v1/satellites/25544');
      const data = await res.json();
      const pos = [data.latitude, data.longitude];
      
      setIssData(data);
      setTrackPath(prev => [...prev.slice(-9), pos]); // Last 10 points
      setLastUpdate(new Date());
      setError(null);
    } catch (err) {
      setError('Failed to update location');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchISS();
    const id = setInterval(fetchISS, 5000);
    return () => clearInterval(id);
  }, []);

const MapUpdater = ({ center }) => {
  const map = useMap();

  useEffect(() => {
    if (autoTrack) {
      map.setView(center, 3, { animate: true });
    }
  }, [center,map]); // ← بس center

  return null;
};

  const formatCoord = (c, t) => {
    const dir = t === 'lat' ? (c >= 0 ? 'N' : 'S') : (c >= 0 ? 'E' : 'W');
    return `${Math.abs(c).toFixed(4)}° ${dir}`;
  };

  const issIcon = L.divIcon({
    html: `<div style="background:#00aaff;border:3px solid #66ccff;border-radius:50%;width:22px;height:22px;box-shadow:0 0 15px #00aaff;animation:pulse 1.5s infinite;"></div>`,
    className: '',
    iconSize: [22, 22],
    iconAnchor: [11, 11]
  });

  // Mock upcoming passes (real API: https://api.n2yo.com)
  const upcomingPasses = [
    { date: 'Oct 31', time: '09:59', duration: '11 min', max: '39°' },
    { date: 'Oct 31', time: '16:34', duration: '40 min', max: '72°' },
    { date: 'Nov 02', time: '06:00', duration: '63 min', max: '85°' },
    { date: 'Nov 04', time: '22:03', duration: '1 min', max: '10°' },
  ];

  if (loading) return <LoadingScreen />;

  return (
    <>
      {/* Hero Header */}
      <header className="bg-gradient-to-b from-blue-900 to-blue-800 text-white py-8">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl font-bold mb-2 flex items-center justify-center gap-3">
            <Satellite className="w-12 h-12 text-cyan-400 animate-pulse" />
            ISS Tracker
          </h1>
          <p className="text-xl text-cyan-200">Real Time ISS Tracker Map </p>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-7xl">
        {error && (
          <div className="bg-red-600/20 border border-red-500 rounded-xl p-4 text-center mb-6 text-red-200">
            {error}
          </div>
        )}

        {/* Map Card */}
        <div className="bg-white rounded-2xl shadow-xl p-6 mb-8 border border-gray-200">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
              <Globe className="w-7 h-7 text-blue-600" />
              Live ISS Location
            </h2>
     
          </div>

          <div className="rounded-xl overflow-hidden border border-gray-300" style={{ height: '500px' }}>
            <MapContainer center={[0, 0]} zoom={3} style={{ height: '100%', width: '100%' }}>
              <TileLayer
                url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                attribution='&copy; OpenStreetMap contributors'
              />
              {issData && (
                <>
                  <Marker position={[issData.latitude, issData.longitude]} icon={issIcon}>
                    <Popup>
                      <div className="text-center">
                        <b className="text-lg">International Space Station</b>
                        <p>Speed: {Math.round(issData.velocity)} km/h</p>
                        <p>Altitude: {Math.round(issData.altitude)} km</p>
                      </div>
                    </Popup>
                  </Marker>
                  <Polyline 
                    positions={trackPath} 
                    color="#00aaff" 
                    weight={4} 
                    opacity={0.8}
                    dashArray="10, 10"
                  />
                  <MapUpdater center={[issData.latitude, issData.longitude]} />
                </>
              )}
            </MapContainer>
          </div>
          <p className="text-center text-sm text-gray-600 mt-3">
            The blue dot and dashed path show the current ISS position and recent orbit
          </p>
        </div>

        {/* Info Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <InfoCard icon={<MapPin className="w-8 h-8 text-green-600" />} title="Current Location">
            <p>Latitude: <span className="font-mono text-green-700">{formatCoord(issData.latitude, 'lat')}</span></p>
            <p>Longitude: <span className="font-mono text-blue-700">{formatCoord(issData.longitude, 'lng')}</span></p>
          </InfoCard>

          <InfoCard icon={<Gauge className="w-8 h-8 text-orange-600" />} title="Speed">
            <p className="text-3xl font-bold text-orange-600">{Math.round(issData.velocity)} km/h</p>
            <p className="text-gray-600">Orbital velocity</p>
          </InfoCard>

          <InfoCard icon={<Activity className="w-8 h-8 text-purple-600" />} title="Altitude">
            <p className="text-3xl font-bold text-purple-600">{Math.round(issData.altitude)} km</p>
            <p className="text-gray-600">Above Earth</p>
          </InfoCard>
        </div>

        {/* Upcoming Passes */}
        <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200">
          <h2 className="text-2xl font-bold text-gray-800 mb-4 flex items-center gap-2">
            Next ISS Passes Over Your Location
            {/* <ChevronRight className="w-6 h-6 text-blue-600" /> */}
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {upcomingPasses.map((pass, i) => (
              <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-gray-200 text-center">
                <p className="font-semibold text-gray-700">{pass.date}</p>
                <p className="text-2xl font-bold text-blue-600">{pass.time}</p>
                <p className="text-sm text-gray-600">Duration: {pass.duration}</p>
                <p className="text-sm font-medium text-green-600">Max: {pass.max}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Status Bar */}
        <div className="mt-8 text-center bg-gradient-to-r from-green-50 to-cyan-50 rounded-xl p-4 border border-green-200">
          <div className="flex items-center justify-center gap-2">
            <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse"></div>
            <span className="font-medium text-green-800">
              ISS Connected — Data updated every 5 seconds
            </span>
            <Clock className="w-4 h-4 text-green-600 ml-2" />
            <span className="text-sm text-gray-600">
              Last update: {lastUpdate.toLocaleTimeString('en-US')}
            </span>
          </div>
        </div>

        <p className="text-center text-xs text-gray-500 mt-6">
          Built by <b>CosmoCoder Nihal</b> • Data from <a href="https://wheretheiss.at" className="underline">wheretheiss.at</a>
        </p>
      </div>
    </>
  );
};

// Loading Screen (like isstracker.pl)
const LoadingScreen = () => (
  <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-cyan-900 flex items-center justify-center p-4">
    <div className="text-center">
      <Satellite className="w-20 h-20 text-cyan-400 animate-spin mx-auto mb-6" />
      <h2 className="text-3xl font-bold text-white mb-2">Application is loading...</h2>
      <p className="text-cyan-200">Please wait while we connect to the ISS...</p>
    </div>
  </div>
);

// Reusable Card
const InfoCard = ({ icon, title, children }) => (
  <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-200 hover:shadow-xl transition-shadow">
    <div className="flex items-center mb-3">
      {icon}
      <h3 className="text-lg font-semibold text-gray-800 ml-2">{title}</h3>
    </div>
    <div className="text-sm space-y-1">{children}</div>
  </div>
);

export default ISSTracker;

